/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ECharts from "echarts-for-react";
import { Box, Chip, Tab, Tabs } from "@mui/material";
import MainCard from "components/MainCard";
import useConfig from "hooks/useConfig";
import { getEquipmentOpLogsStatsOpStatusDurationQueryString } from "components/axios/equipmentAxios";
import { buildOption, buildTimeAxis, mapDurationToAxis } from "data/dateUtils";

// const DELAY_TIME = 60000;
const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function DashboardEquipmentDurationGraphs({ equipmentId, height=200, target='dashboard' }) {
    const { t } = useTranslation();
    const { mode } = useConfig();

    const textColor = mode === "light" ? "#262626" : "#ffffff";
    const bgColor = mode === "light" ? "#f9f9fa" : "#2f2f2f";

    /* ---------------- 상태 ---------------- */
    const [tab, setTab] = useState("DAY");
    const graphParam =
        "?equipment.id=" + equipmentId;
    // const paramType = "DAY";

    const BASE_OPTION = {
        tooltip: { trigger: "axis" },
        grid: { left: 40, right: 20, top: 40, bottom: 30 },
        xAxis: { type: "category", data: [] },
        yAxis: { type: "value" },
        series: [],
    };

    /* ---------------- refs ---------------- */
    const chartRef = useRef(null);
    const pollingRef = useRef(null);
    const prevDataKeyRef  = useRef(null);
    const prevThemeRef = useRef(mode); // light | dark
    const prevTabRef = useRef(tab);
    const [axis, setAxis] = useState([])

    function formatDuration(totalMinutes) {
        if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "0h";
        if (!totalMinutes || totalMinutes <= 0) return "0h";

        // 🔒 부동소수점 오차 제거
        // const minutes = Math.round(totalMinutes);

        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;

        if (m === 0) return `${h}${t('message.common.text.time')}`;
        return `${h}${t('message.common.text.time')} ${m}${t('message.common.text.minute')}`;
    }

    useEffect(() => {
        if (!equipmentId) return;
        const load = async () => {
            const data = await getEquipmentOpLogsStatsOpStatusDurationQueryString(
                tab,
                graphParam + "&opStatus=ACTIVE"
            );
            // console.log(data)
            const tabChanged = prevTabRef.current !== tab;

            const currentAxis = buildTimeAxis(tab);
            setAxis(currentAxis);

            // console.log(currentAxis, tab)
            const series = mapDurationToAxis(currentAxis, data ?? [], tab, t);
            const totalMap = {};
            series.forEach((s) => {
                totalMap[s.name] = (s.data || []).reduce(
                    (sum, v) => sum + (Number(v) || 0),
                    0
                );
            });
            // console.log(series)

            // 🔒 동일 데이터면 skip
            const chart = chartRef.current?.getEchartsInstance();
            if (!chart) return;

            const dataKey = JSON.stringify(series);
            const themeChanged = prevThemeRef.current !== mode;
            const dataChanged = prevDataKeyRef.current !== dataKey;

            // 공통 옵션 생성 헬퍼 (중복 코드 제거용)
            const applyChartOption = (isMerge = false) => {
                const option = buildOption(currentAxis, series, textColor, bgColor, tab, t, "bar");
                option.legend = {
                    ...option.legend,
                    formatter: (name) => {
                        const rawTotal = totalMap[name];
                        if (rawTotal === undefined) return name;
                        const totalMinutes = tab === "DAY" ? Math.floor(rawTotal) : Math.floor(rawTotal * 60);
                        return `${name} (${formatDuration(totalMinutes)})`;
                    },
                    textStyle: { color: textColor },
                    backgroundColor: bgColor,
                };
                option.textStyle.color = textColor;
                chart.setOption(option, { notMerge: !isMerge });
            };

            // 최초 렌더
            if (!prevDataKeyRef.current) {
                prevDataKeyRef.current = dataKey;
                prevThemeRef.current = mode;
                prevTabRef.current = tab;
                applyChartOption(false);
                return;
            }

            // 탭 변경 → 축 변경 → 무조건 전체 렌더
            if (tabChanged) {
                prevTabRef.current = tab;
                prevDataKeyRef.current = dataKey;
                applyChartOption(false);
                return;
            }

            // 테마 변경
            if (themeChanged) {
                prevThemeRef.current = mode;
                prevDataKeyRef.current = dataKey;
                applyChartOption(false);
                return;
            }

            // 데이터만 변경 (polling)
            if (dataChanged) {
                prevDataKeyRef.current = dataKey;
                chart.setOption({
                    series,
                    legend: {
                        formatter: (name) => {
                            const rawTotal = totalMap[name];
                            if (rawTotal === undefined) return name;

                            const totalMinutes =
                                tab === "DAY"
                                    ? Math.floor(rawTotal)
                                    : Math.floor(rawTotal * 60);

                            return `${name} (${formatDuration(totalMinutes)})`;
                        },
                    },
                }, { notMerge: false });
                // chart.setOption({ series, }, { notMerge: false }); // 깜빡임 없음
            }
        };

        load();
        pollingRef.current = setInterval(load, DELAY_TIME); // ⏱ 1분

        return () => {
            clearInterval(pollingRef.current);
        };
    }, [tab, mode, equipmentId]);

    return (
        <React.Fragment>
            <div className="row" style={{marginTop:"15px", width:"100%"}}>
                <div className="mixed-chart">
                    <div className='alert_phrase'>
                        <div style={{display: "flex", justifyContent:"space-between", alignItems: "center", marginTop: "-10px", marginBottom: "10px", width: "100%",borderRadius: "0", borderBottom: "1px solid #636363"}}>
                            <div style={{padding:"0 10px", textAlign:"center"}}>
                                <Chip label={`${t('message.common.chip.uptime')}`} color="primary" />
                            </div>
                            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{alignItems : "center", ".MuiTabs-flexContainer" : {justifyContent:"center"}}}>
                                <Tab label={t("message.common.text.daily")} value="DAY" className="graphTab"
                                    style={{border:`${tab === "DAY" ? "3px solid #0061d0": "1px solid #0061d0"}`, margin:"unset", minWidth:"50px"}} />
                                <Tab label={t("message.common.text.monthly")} value="MONTH" className="graphTab"
                                    style={{border:`${tab === "MONTH" ? "3px solid #0061d0": "1px solid #0061d0"}`, margin:"unset", minWidth:"50px"}} />
                                <Tab label={t("message.common.text.years")} value="YEAR" className="graphTab"
                                    style={{border:`${tab === "YEAR" ? "3px solid #0061d0": "1px solid #0061d0"}`, margin:"unset", minWidth:"50px"}} />
                            </Tabs>
                        </div>
                        <Box>
                                <MainCard content={false}>
                                <ECharts
                                    ref={chartRef}
                                    option={BASE_OPTION}
                                    notMerge={true}
                                    lazyUpdate={true}
                                    opts={{ renderer: "svg", height: height }}
                                    style={{height:`${height}px`}}
                                />
                                </MainCard>
                        </Box>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default DashboardEquipmentDurationGraphs;
